import { AlertTriangle, Clock } from 'lucide-react';
import type { Demande } from '../../types';
import { BadgesDemande, horaireEtLieu } from './CarteCalendrier';
import { MiniFrise } from './MiniFrise';
import {
  complementCarte, complementCarteGestion, etatFrise, phraseCarte, phraseCarteGestion, remplacantEnVigueur, urgenceDemande, vueDeLaDemande,
  type ElementJour, type Trou, type VoixCarte,
} from './elementsJour';
import { libelleEcheance, type SeuilsUrgence } from './urgence';

// Le détail du jour du Tableau de bord, ouvert par-dessus le calendrier : pour chaque demande, ce que la carte montre ET ce qu'elle n'a pas la
// place de dire (le lieu, la suite, le/la remplaçant·e), puis le geste utile. Même ordre que la case (les heures). « Mes demandes » : écrit pour
// la personne absente ; gestion (DECISIONS §27) : les mêmes blocs, à la 3e personne, avec les noms.

export interface DetailJourProps {
  voix: VoixCarte;
  elements: ElementJour[];
  /** « Mes demandes » : « Vous : absent·e » ce jour-là. */
  absent: boolean;
  /** Gestion : les intervenant·e·s absent·e·s ce jour-là (une fois chacun·e). */
  absents: string[];
  maintenant: Date;
  seuils: SeuilsUrgence;
  /** La demande ou la séance cliquée : mise en avant, et c'est elle qui reçoit le focus à l'ouverture. */
  cleEnAvant: string | null;
  onOuvrirDemande: (d: Demande) => void;
  onCreer: (t: Trou) => void;
  /** Brouillon repéré dans Tipee : le même geste que la liste (« Envoyer ma demande de remplacement »). Jamais pour la gestion. */
  onEnvoyerTipee: (d: Demande) => void;
  envoiEnCours: boolean;
  /** Une phrase à dire dans la fenêtre même (le calendrier est caché derrière) : échec de l'envoi, par exemple. */
  message: string | null;
}

export function idDetail(cle: string): string { return `pdet-${cle}`; }

export function DetailJour(p: DetailJourProps) {
  if (p.elements.length === 0 && !p.absent && p.absents.length === 0) return <p className="text-sm text-ink-3" style={{ margin: 0 }}>Rien ce jour-là.</p>;
  return (
    <div className="cal-pdets">
      {p.message && <div className="badge badge-err" role="alert" style={{ display: 'block' }}>{p.message}</div>}
      {p.absent && <div className="cal-absent">Vous : absent·e</div>}
      {p.absents.length > 0 && <div className="cal-absent">{p.absents.length > 1 ? `${p.absents.length} absent·e·s` : '1 absent·e'} : {p.absents.join(', ')}</div>}
      {p.elements.map((e) => {
        const enAvant = e.cle === p.cleEnAvant;
        if (e.kind === 'trou') return <BlocTrou key={e.cle} cle={e.cle} trou={e.trou} enAvant={enAvant} voix={p.voix} onCreer={p.onCreer} />;
        return <BlocDemande key={e.cle} cle={e.cle} d={e.demande} enAvant={enAvant} p={p} />;
      })}
    </div>
  );
}

function Titre({ cle, nom, couleur, detail }: { cle: string; nom: string; couleur?: string | null; detail: string }) {
  return (
    <h3 className="cal-pdet-t" id={idDetail(cle)} tabIndex={-1}>
      <span className="pole-dot cal-dot" style={{ background: couleur ?? 'var(--line-strong)' }} aria-hidden="true" />
      {nom}{detail && <span className="cal-pdet-meta">{detail}</span>}
    </h3>
  );
}

function BlocTrou({ cle, trou: t, enAvant, voix, onCreer }: { cle: string; trou: Trou; enAvant: boolean; voix: VoixCarte; onCreer: (t: Trou) => void }) {
  const passee = t.joursRestants < 0;
  const urgent = t.niveau === 'urgent';
  const gestion = voix === 'gestion';
  return (
    <section className={`cal-pdet${enAvant ? ' cal-pdet--focus' : ''}`} aria-labelledby={idDetail(cle)}>
      <Titre cle={cle} nom={t.atelierNom} couleur={t.poleCouleur} detail={horaireEtLieu(t.heureDebut, null, t.lieu)} />
      <div className="cal-pdet-badges">
        {passee
          ? <span className="badge"><Clock className="cal-icone" aria-hidden="true" />Séance passée</span>
          : <span className={`badge ${urgent ? 'badge-err' : 'badge-warn'}`}><AlertTriangle className="cal-icone" aria-hidden="true" />À couvrir · {libelleEcheance(t.joursRestants)}</span>}
      </div>
      <p className="cal-pdet-p">
        {gestion
          ? <>{passee ? 'Séance passée sans demande de remplacement.' : 'Aucune demande pour cette séance.'} Titulaire absent·e : <b>{t.titulaireNom}</b>.</>
          : passee ? "Séance passée sans demande de remplacement : prévenez l'équipe gestion si besoin." : 'Aucune demande pour cette séance : personne ne vous remplace encore.'}
      </p>
      {!passee && (
        <div className="cal-pdet-actions">
          <button type="button" className="btn btn-sm btn-accent" aria-describedby={idDetail(cle)} onClick={() => onCreer(t)}>{urgent ? 'Urgent : créer la demande' : 'Créer la demande'}</button>
        </div>
      )}
    </section>
  );
}

function BlocDemande({ cle, d, enAvant, p }: { cle: string; d: Demande; enAvant: boolean; p: DetailJourProps }) {
  const gestion = p.voix === 'gestion';
  const urgence = urgenceDemande(d, p.maintenant, p.seuils);
  const phrase = gestion ? phraseCarteGestion(d, p.maintenant) : phraseCarte(d, p.maintenant);
  const complement = gestion ? complementCarteGestion(d, p.maintenant) : complementCarte(d, p.maintenant);
  const remplacant = remplacantEnVigueur(d);
  const tipeeAEnvoyer = !gestion && d.statut === 'brouillon' && d.source === 'tipee';
  const vue = vueDeLaDemande(d, p.seuils, p.maintenant);
  const frise = etatFrise(d.statut, d.date_atelier, p.maintenant, vue);
  return (
    <section className={`cal-pdet${enAvant ? ' cal-pdet--focus' : ''}`} aria-labelledby={idDetail(cle)}>
      <Titre cle={cle} nom={d.atelier_nom ?? 'Atelier'} couleur={d.pole_couleur} detail={horaireEtLieu(d.atelier_heure_debut, d.atelier_heure_fin, d.atelier_lieu)} />
      <div className="cal-pdet-badges"><BadgesDemande d={d} urgence={urgence} vue={vue} /></div>
      {frise.phase != null && <MiniFrise demande={d} peutGerer={gestion} seuils={p.seuils} />}
      {/* Terminé : la mini-frise dit déjà « Remplacement effectué » — pas deux fois. */}
      {!frise.termine && <p className="cal-pdet-p"><b>{phrase}</b>{complement ? `${/[.…?!]$/.test(phrase) ? '' : '.'} ${complement}` : ''}</p>}
      {gestion
        ? <p className="cal-pdet-p">Absent·e : <b>{d.absent_nom}</b>{remplacant && <> · Remplaçant·e : <b>{remplacant}</b></>}</p>
        : remplacant && !phrase.includes(remplacant) && <p className="cal-pdet-p">Remplaçant·e : {remplacant}</p>}
      <div className="cal-pdet-actions">
        {tipeeAEnvoyer && (
          <button type="button" className="btn btn-sm btn-accent" aria-describedby={idDetail(cle)} aria-busy={p.envoiEnCours || undefined} onClick={() => p.onEnvoyerTipee(d)}>
            Envoyer ma demande de remplacement
          </button>
        )}
        <button type="button" className="btn btn-sm" aria-describedby={idDetail(cle)} onClick={() => p.onOuvrirDemande(d)}>Ouvrir la demande</button>
      </div>
    </section>
  );
}
